import React, {useState} from 'react';
import addeparLogo from '../../assets/images/connect-logos/addepar.png';
import billLogo from '../../assets/images/connect-logos/bill.png';
import charlesSchwabLogo from '../../assets/images/connect-logos/charles-schwab.png';
import northernTrustLogo from '../../assets/images/connect-logos/northern-trust.png';
import pershingLogo from '../../assets/images/connect-logos/pershing.png';
import plaidLogo from '../../assets/images/connect-logos/plaid.png';
import rampLogo from '../../assets/images/connect-logos/ramp.png';
import {BillModal} from '../../components/Navbar/BillModal';
import {RampModal} from '../../components/Navbar/RampModal';
import {CustodianModal} from '../../components/Navbar/CustodianModal';
import {BridgeFTLinkModal} from '../../components/Navbar/BridgeFTModal';
import {PlaidLinkContent} from '../../components/Plaid/PlaidLinker';
import {useAuth} from '../../hooks/use-auth';
import '../../styles/plaid.css';

// Read from the environment so a production build points at the production
// Addepar tenant. This was hardcoded to the dev tenant and dev redirect, which
// meant production customers clicking Addepar were sent to Forest's dev
// instance. The dev URL stays as the fallback so local builds are unchanged.
// Ramp has no production credentials or deployed functions yet, so the tile
// would fail on click. Hidden by setting REACT_APP_ENABLE_RAMP=false in that
// environment; defaults to shown so dev and local are unchanged.
const RAMP_ENABLED = process.env.REACT_APP_ENABLE_RAMP !== 'false';

const ADDEPAR_OAUTH_URL =
  process.env.REACT_APP_ADDEPAR_OAUTH ||
  'https://forestsystems.clientdev.addepar.com/oauth2/authorize?response_type=code&scope=entities+portfolio+profile+transactions+transactions_write&client_id=forestsys_dev&redirect_uri=https%3A%2F%2Fforest-systems-plaid-dev.web.app%2FaddeparConnect';

const ConnectionCard = ({logoSrc, logoAlt, description, onClick, disabled}) => (
  <div
    className={`connection-card${disabled ? ' connection-card-disabled' : ''}`}
    onClick={disabled ? undefined : onClick}
  >
    <div className='connection-card-logo'>
      <img src={logoSrc} alt={logoAlt} />
      {disabled && (
        <div className='connection-card-coming-soon'>Coming soon</div>
      )}
    </div>
    <div className='connection-card-description'>{description}</div>
  </div>
);

const SectionHeader = ({title}) => (
  <div className='connection-section-header'>{title}</div>
);

export default function AddConnectionPage({user}) {
  const {
    state: {client},
  } = useAuth();

  const [plaidActive, setPlaidActive] = useState(false);
  const [billActive, setBillActive] = useState(false);
  const [rampActive, setRampActive] = useState(false);
  const [bridgeActive, setBridgeActive] = useState(false);
  // FOR-2250: which direct custodian's intake window is open, or null.
  const [custodian, setCustodian] = useState(null);

  const openAddepar = () => {
    window.location.href = ADDEPAR_OAUTH_URL;
  };

  const showPlaid = !!client?.plaid;
  const showBill = !!client?.bill;
  const showBridge = !!client?.bridgeft;
  const showAddepar = !!client?.addepar;

  return (
    <div className='page-container'>
      {showPlaid && (
        <>
          <SectionHeader title='Connect Bank Accounts' />
          <div className='connection-card-grid'>
            <ConnectionCard
              logoSrc={plaidLogo}
              logoAlt='Plaid'
              description={
                <>
                  Click on the logo to connect a bank account with{' '}
                  <strong>Plaid</strong>.<br />
                  <br />
                  Use your online banking credentials to access your banking
                  transactions through Forest Connect and Plaid.
                </>
              }
              onClick={() => setPlaidActive(true)}
            />
          </div>
        </>
      )}

      {/* Direct custodian feeds do not depend on Plaid or BridgeFT. */}
      <>
          <SectionHeader title='Connect Custodian Accounts' />
          <div className='connection-card-grid'>
            {showPlaid && (
              <ConnectionCard
                logoSrc={plaidLogo}
                logoAlt='Plaid'
                description={
                  <>
                    Click on the logo to connect a custodian account with{' '}
                    <strong>Plaid</strong>.<br />
                    <br />
                    Use your online banking credentials to access your
                    investment transactions through Forest Connect and Plaid.
                    Note, Transaction Types are limited.
                  </>
                }
                onClick={() => setPlaidActive(true)}
              />
            )}
            {/* Northern Trust is a direct custodian feed like Pershing and
                Schwab, so it is no longer gated on the BridgeFT flag and opens
                the same intake window they do. */}
            <ConnectionCard
              logoSrc={northernTrustLogo}
              logoAlt='Northern Trust'
              description={
                <>
                  Click on the logo to connect a custodian account from{' '}
                  <strong>Northern Trust</strong>.<br />
                  <br />
                  Receive account transactions and security pricing
                  information.
                </>
              }
              onClick={() =>
                setCustodian({key: 'northern-trust', name: 'Northern Trust'})
              }
            />
            <ConnectionCard
              logoSrc={pershingLogo}
              logoAlt='Pershing'
              description={
                <>
                  Click on the logo to connect a custodian account from{' '}
                  <strong>Pershing</strong>.<br />
                  <br />
                  Receive account transactions and security pricing
                  information.
                </>
              }
              onClick={() =>
                setCustodian({key: 'pershing', name: 'Pershing'})
              }
            />
            <ConnectionCard
              logoSrc={charlesSchwabLogo}
              logoAlt='Charles Schwab'
              description={
                <>
                  Click on the logo to connect a custodian account from{' '}
                  <strong>Charles Schwab</strong>.<br />
                  <br />
                  Receive account transactions and security pricing
                  information.
                </>
              }
              onClick={() =>
                setCustodian({key: 'schwab', name: 'Charles Schwab'})
              }
            />
          </div>
      </>

      {showBill && (
        <>
          <SectionHeader title='Connect Bill Pay Software' />
          <div className='connection-card-grid'>
            <ConnectionCard
              logoSrc={billLogo}
              logoAlt='BILL'
              description={
                <>
                  Click on the logo to connect your Forest database with{' '}
                  <strong>BILL</strong>.<br />
                  <br />
                  Receive bill pay and credit card transaction already in
                  double-entry accounting format.
                </>
              }
              onClick={() => setBillActive(true)}
            />
            {RAMP_ENABLED && (
            <ConnectionCard
              logoSrc={rampLogo}
              logoAlt='Ramp'
              description={
                <>
                  Click on the logo to connect your Forest database with{' '}
                  <strong>Ramp</strong>.<br />
                  <br />
                  Receive bill pay and credit card transaction already in
                  double-entry accounting format.
                </>
              }
              onClick={() => setRampActive(true)}
            />
            )}
          </div>
        </>
      )}

      {showAddepar && (
        <>
          <SectionHeader title='Connect Investment Software' />
          <div className='connection-card-grid'>
            <ConnectionCard
              logoSrc={addeparLogo}
              logoAlt='Addepar'
              description={
                <>
                  Click on the logo to connect your Forest database with{' '}
                  <strong>Addepar</strong>.<br />
                  <br />
                  Send and received transactions between Addepar and Forest.
                </>
              }
              onClick={openAddepar}
            />
          </div>
        </>
      )}

      {plaidActive && (
        <PlaidLinkContent
          active={plaidActive}
          setActive={setPlaidActive}
          user={user}
        />
      )}
      {billActive && <BillModal active={billActive} setActive={setBillActive} />}
      {rampActive && <RampModal active={rampActive} setActive={setRampActive} />}
      {bridgeActive && (
        <BridgeFTLinkModal active={bridgeActive} setActive={setBridgeActive} />
      )}
      {custodian && (
        <CustodianModal
          active={!!custodian}
          setActive={open => !open && setCustodian(null)}
          custodian={custodian.key}
          displayName={custodian.name}
        />
      )}
    </div>
  );
}
